import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, CheckCircle2, Cake, Award, FileDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtMinutes, fmtDate, fmtDateShort } from "@/lib/format";
import { downloadBlob } from "@/lib/download";
import { PageHeader, EmptyState } from "@/components/Page";

export default function Freigaben() {
  const [sessions, setSessions] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [delRequests, setDelRequests] = useState([]);
  const [celebrations, setCelebrations] = useState([]);

  const load = async () => {
    const [s, v, c] = await Promise.all([api.get("/sessions/pending"), api.get("/vacations"), api.get("/celebrations/upcoming")]);
    setSessions(s.data || []);
    const all = v.data || [];
    setVacations(all.filter((x) => x.status === "pending"));
    setDelRequests(all.filter((x) => x.deletion_requested));
    setCelebrations(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const approveSession = async (id) => {
    try { await api.post(`/sessions/${id}/approve`); toast.success("Zeit freigegeben"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rejectSession = async (id) => {
    try { await api.delete(`/sessions/${id}`); toast.success("Eintrag abgelehnt"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const actVacation = async (id, action) => {
    try { await api.post(`/vacations/${id}/${action}`); toast.success(action === "approved" ? "Urlaub genehmigt" : "Urlaub abgelehnt"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const actVacationDeletion = async (id, action) => {
    try { await api.post(`/vacations/${id}/deletion/${action}`); toast.success(action === "approve" ? "Löschung genehmigt – Tage zurückgebucht" : "Löschung abgelehnt"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Freigaben" subtitle="Genehmige Zeitnachträge und Urlaubsanträge." testid="freigaben-header" />
      <Tabs defaultValue="zeiten">
        <TabsList className="h-auto p-1 bg-slate-100 rounded-lg mb-4">
          <TabsTrigger value="zeiten" data-testid="tab-zeiten">Zeiten ({sessions.length})</TabsTrigger>
          <TabsTrigger value="urlaub" data-testid="tab-urlaub">Urlaub ({vacations.length + delRequests.length})</TabsTrigger>
          <TabsTrigger value="anlaesse" data-testid="tab-anlaesse">Anlässe ({celebrations.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="zeiten">
          <div className="grid gap-3">
            {sessions.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={CheckCircle2} title="Keine offenen Zeit-Freigaben" testid="zeiten-freigaben-empty" /></Card>
            ) : sessions.map((s) => (
              <Card key={s.id} className="p-4 border-slate-200 rounded-xl flex items-center justify-between gap-4" data-testid={`pending-session-${s.id}`}>
                <div>
                  <div className="font-medium text-slate-900">{s.user_name}</div>
                  <div className="text-sm text-slate-500">{fmtDateShort(s.date)} · {s.start}–{s.end} · {fmtMinutes(s.minutes)}{s.note ? ` · ${s.note}` : ""}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => approveSession(s.id)} data-testid={`approve-session-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => rejectSession(s.id)} data-testid={`reject-session-${s.id}`} className="text-rose-600 hover:bg-rose-50 border-rose-200"><X className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="urlaub">
          <div className="grid gap-3">
            {vacations.length === 0 && delRequests.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={CheckCircle2} title="Keine offenen Urlaubsanträge" testid="urlaub-freigaben-empty" /></Card>
            ) : (<>
              {vacations.map((v) => (
              <Card key={v.id} className="p-4 border-slate-200 rounded-xl flex items-center justify-between gap-4" data-testid={`pending-vacation-${v.id}`}>
                <div>
                  <div className="font-medium text-slate-900">{v.user_name}</div>
                  <div className="text-sm text-slate-500">{fmtDate(v.start_date)} – {fmtDate(v.end_date)}{v.category && v.category !== "urlaub" ? ` · ${v.category === "sonderurlaub" ? "Sonderurlaub" : "Sonstige"}` : ""}{v.half_day ? ` · Halber Tag${v.half_day_period ? ` (${v.half_day_period === "vormittag" ? "Vormittag" : "Nachmittag"})` : ""}` : ""}{v.note ? ` · ${v.note}` : ""}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => actVacation(v.id, "approved")} data-testid={`approve-vacation-${v.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => actVacation(v.id, "rejected")} data-testid={`reject-vacation-${v.id}`} className="text-rose-600 hover:bg-rose-50 border-rose-200"><X className="h-4 w-4" /></Button>
                </div>
              </Card>
              ))}
              {delRequests.map((v) => (
              <Card key={v.id} className="p-4 border-amber-200 bg-amber-50/40 rounded-xl flex items-center justify-between gap-4" data-testid={`deletion-request-${v.id}`}>
                <div>
                  <div className="font-medium text-slate-900">{v.user_name}</div>
                  <div className="text-sm text-slate-500">{fmtDate(v.start_date)} – {fmtDate(v.end_date)} · <span className="text-amber-700 font-medium">Löschung des genehmigten Urlaubs beantragt</span></div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => actVacationDeletion(v.id, "approve")} data-testid={`approve-deletion-${v.id}`} className="bg-rose-600 hover:bg-rose-700 text-white" title="Löschung genehmigen (Tage zurückbuchen)"><Trash2 className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => actVacationDeletion(v.id, "reject")} data-testid={`reject-deletion-${v.id}`} className="text-slate-600 hover:bg-slate-50" title="Löschung ablehnen (Urlaub behalten)"><X className="h-4 w-4" /></Button>
                </div>
              </Card>
              ))}
            </>)}
          </div>
        </TabsContent>

        <TabsContent value="anlaesse">
          <div className="grid gap-3">
            {celebrations.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={Cake} title="Keine Geburtstage oder Jubiläen in den nächsten 10 Tagen" testid="anlaesse-empty" /></Card>
            ) : celebrations.map((c) => {
              const isBirthday = c.type === "birthday";
              const Icon = isBirthday ? Cake : Award;
              const when = c.days_until === 0 ? "heute" : c.days_until === 1 ? "morgen" : `in ${c.days_until} Tagen`;
              return (
                <Card key={`${c.type}-${c.user_id}`} className="p-4 border-slate-200 rounded-xl flex items-center justify-between gap-4" data-testid={`celebration-${c.type}-${c.user_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isBirthday ? "bg-pink-50 text-pink-600" : "bg-amber-50 text-amber-600"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{c.user_name}</div>
                      <div className="text-sm text-slate-500">
                        {isBirthday ? `Geburtstag${c.age ? ` (wird ${c.age})` : ""}` : `${c.years}-jähriges Jubiläum`} · {fmtDate(c.date)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!isBirthday && (
                      <Button size="sm" variant="outline" data-testid={`celebration-cert-${c.user_id}`} onClick={async () => { try { await downloadBlob(`/celebrations/${c.user_id}/certificate?years=${c.years}`, `Urkunde_${c.user_name}.pdf`); } catch (e) { toast.error("Urkunde konnte nicht erstellt werden"); } }}>
                        <FileDown className="h-4 w-4 mr-1" /> Urkunde
                      </Button>
                    )}
                    <span className={`text-sm font-semibold ${c.days_until <= 1 ? "text-rose-600" : "text-slate-500"}`} data-testid={`celebration-when-${c.type}-${c.user_id}`}>{when}</span>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
